import {
  PUBLIC_COGNITO_CLIENT_ID,
  PUBLIC_COGNITO_DOMAIN,
  PUBLIC_COGNITO_REDIRECT_URI
} from '$env/static/public';
import type { AuthProvider, AuthSession, User } from './auth-provider';
import {
  consumeArtifacts,
  generateCodeChallenge,
  generateCodeVerifier,
  generateState,
  storeArtifacts
} from './pkce';
import { clearSession, persistSession, readSession } from './token-storage';

// --- Types Cognito ---

type TokenResponse = {
  access_token: string;
  id_token: string;
  // Cognito ne retourne PAS de refresh_token au refresh par defaut (rotation desactivee).
  // On reutilise alors celui qu'on avait deja.
  refresh_token?: string;
  token_type: 'Bearer';
  expires_in: number; // secondes (typiquement 900 = 15 min)
};

type IdTokenClaims = {
  sub: string;
  email?: string;
  name?: string;
  'cognito:username'?: string;
  /**
   * Pour un user federe (GitLab), Cognito ajoute ce claim. Format observe :
   * - Array: [{providerName: "GitLab", providerType: "OIDC", userId: "..."}]
   * - String JSON: '[{"providerName":"GitLab",...}]' (variant ancien)
   * Pour un compte local Cognito, le claim est absent.
   */
  identities?: Array<{ providerName?: string }> | string;
};

/** Determine si la session vient de la federation GitLab. Robuste aux deux formats Cognito. */
function detectGitLabIdentity(identities: IdTokenClaims['identities']): boolean {
  if (!identities) return false;
  let list: Array<{ providerName?: string }>;
  if (typeof identities === 'string') {
    try {
      list = JSON.parse(identities);
    } catch {
      return false;
    }
  } else {
    list = identities;
  }
  return list.some((id) => id.providerName === 'GitLab');
}

// --- URL builders ---

function authorizeUrl(codeChallenge: string, state: string): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: PUBLIC_COGNITO_CLIENT_ID,
    redirect_uri: PUBLIC_COGNITO_REDIRECT_URI,
    scope: 'openid email profile',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    state
  });
  return `${PUBLIC_COGNITO_DOMAIN}/oauth2/authorize?${params}`;
}

function logoutUrl(): string {
  // logout_uri doit etre une SignOutUrl autorisee dans l'App Client (sinon Cognito refuse).
  // On rentre sur l'origine de redirect_uri, route '/' (home anonyme).
  const origin = new URL(PUBLIC_COGNITO_REDIRECT_URI).origin;
  const params = new URLSearchParams({
    client_id: PUBLIC_COGNITO_CLIENT_ID,
    logout_uri: `${origin}/`
  });
  return `${PUBLIC_COGNITO_DOMAIN}/logout?${params}`;
}

// --- Token endpoint ---

async function postToken(body: URLSearchParams): Promise<TokenResponse> {
  const resp = await fetch(`${PUBLIC_COGNITO_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body
  });
  if (!resp.ok) {
    const detail = await resp.text();
    throw new Error(`Cognito /oauth2/token ${resp.status}: ${detail}`);
  }
  return resp.json();
}

function exchangeAuthorizationCode(code: string, codeVerifier: string): Promise<TokenResponse> {
  return postToken(
    new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: PUBLIC_COGNITO_CLIENT_ID,
      code,
      redirect_uri: PUBLIC_COGNITO_REDIRECT_URI,
      code_verifier: codeVerifier
    })
  );
}

function exchangeRefreshToken(refreshToken: string): Promise<TokenResponse> {
  return postToken(
    new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: PUBLIC_COGNITO_CLIENT_ID,
      refresh_token: refreshToken
    })
  );
}

// --- JWT decode (sans verification de signature) ---

/**
 * Decode le payload d'un JWT pour lire les claims utilisateur (email, name, sub).
 * La signature est verifiee cote API Gateway au moment des appels backend ; ici on fait
 * confiance au token qu'on vient juste de recevoir directement de Cognito sur HTTPS.
 */
function decodeJwtPayload<T>(jwt: string): T {
  const segment = jwt.split('.')[1];
  if (!segment) throw new Error('invalid JWT: missing payload');
  const padded = segment + '='.repeat((4 - (segment.length % 4)) % 4);
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  // atob rend une chaine binaire Latin1 : un claim non-ASCII (nom federe avec accents,
  // caracteres CJK) serait corrompu sans re-decodage UTF-8 explicite des octets.
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

function buildSession(tokens: TokenResponse, fallbackRefreshToken?: string): AuthSession {
  const claims = decodeJwtPayload<IdTokenClaims>(tokens.id_token);
  const user: User = {
    id: claims.sub,
    email: claims.email ?? '',
    name: claims.name ?? claims['cognito:username'] ?? claims.email ?? 'unknown',
    isGitLabUser: detectGitLabIdentity(claims.identities)
  };
  const refreshToken = tokens.refresh_token ?? fallbackRefreshToken;
  if (!refreshToken) throw new Error('no refresh_token returned and no fallback');
  return {
    user,
    accessToken: tokens.access_token,
    refreshToken,
    expiresAt: Date.now() + tokens.expires_in * 1000
  };
}

// --- Provider ---

export const cognitoAuthProvider: AuthProvider = {
  // logout() fait window.location.assign vers Cognito /logout : le store ne doit PAS setSession(null)
  // ensuite, sinon le $effect du +layout declenche un goto /home?returnTo=... entre le clearSession
  // et le moment ou la nav Cognito prend effet (visible sous forme de flash dans la barre d'URL).
  redirectsOnLogout: true,

  async login(returnTo) {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateState();
    storeArtifacts({ codeVerifier, state, returnTo });
    window.location.assign(authorizeUrl(codeChallenge, state));
  },

  async handleCallback() {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error) {
      const description = params.get('error_description') ?? '';
      throw new Error(`Cognito error: ${error} ${description}`);
    }
    const code = params.get('code');
    const stateReturned = params.get('state');
    if (!code || !stateReturned) throw new Error('callback missing code or state');

    const artifacts = consumeArtifacts();
    if (!artifacts) throw new Error('PKCE artifacts missing - state lost or replay');
    if (stateReturned !== artifacts.state) throw new Error('state mismatch (CSRF protection)');

    const tokens = await exchangeAuthorizationCode(code, artifacts.codeVerifier);
    const session = buildSession(tokens);
    persistSession(session);
    return { session, returnTo: artifacts.returnTo };
  },

  async restoreSilent() {
    return readSession();
  },

  async refresh(refreshToken) {
    if (!refreshToken) throw new Error('no refresh_token to refresh');
    const tokens = await exchangeRefreshToken(refreshToken);
    // Rotation des refresh_tokens desactivee par defaut sur Cognito : on reutilise l'ancien si
    // pas remplace dans la reponse. C'est conforme au comportement standard Cognito.
    const session = buildSession(tokens, refreshToken);
    persistSession(session);
    return session;
  },

  async logout() {
    clearSession();
    // Cognito invalide la session hosted UI cote serveur, puis redirige vers logout_uri (= '/').
    // L'utilisateur revient donc anonyme sur l'origine, sans cookie Cognito residuel.
    window.location.assign(logoutUrl());
  }
};
